import React, { useState, useEffect, useRef } from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { getAccounts, createAccount, updateAccount, deleteAccount } from "../../api/accountingApi";

const ACCOUNT_TYPES = [
  { label: "Asset", value: "Asset" },
  { label: "Liability", value: "Liability" },
  { label: "Equity", value: "Equity" },
  { label: "Revenue", value: "Revenue" },
  { label: "Expense", value: "Expense" },
];

const ChartOfAccountsPage = () => {
  const toast = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", type: "Asset", parent_id: null, description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await getAccounts({ active: true });
      setAccounts(data);
      buildTree(data);
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load accounts" });
    } finally {
      setLoading(false);
    }
  };

  const buildTree = (data, filter = typeFilter) => {
    const filtered = filter ? data.filter((a) => a.type === filter) : data;
    const map = {};
    filtered.forEach((a) => {
      map[a.id] = {
        key: String(a.id),
        data: a,
        children: [],
      };
    });

    const tree = [];
    filtered.forEach((a) => {
      if (a.parent_id && map[a.parent_id]) {
        map[a.parent_id].children.push(map[a.id]);
      } else {
        tree.push(map[a.id]);
      }
    });

    setTreeData(tree);
  };

  const handleTypeFilter = (type) => {
    setTypeFilter(type);
    buildTree(accounts, type);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ code: "", name: "", type: "Asset", parent_id: null, description: "" });
    setDialogVisible(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setForm({
      code: account.code,
      name: account.name,
      type: account.type,
      parent_id: account.parent_id,
      description: account.description || "",
    });
    setDialogVisible(true);
  };

  const handleSave = async () => {
    if (!form.code || !form.name || !form.type) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Code, name, and type are required" });
      return;
    }

    try {
      setSaving(true);
      if (editing) {
        await updateAccount(editing.id, form);
        toast.current?.show({ severity: "success", summary: "Success", detail: "Account updated" });
      } else {
        await createAccount(form);
        toast.current?.show({ severity: "success", summary: "Success", detail: "Account created" });
      }
      setDialogVisible(false);
      loadAccounts();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Failed to save account",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (account) => {
    if (account.is_system) {
      toast.current?.show({ severity: "warn", summary: "Warning", detail: "Cannot delete system accounts" });
      return;
    }

    try {
      await deleteAccount(account.id);
      toast.current?.show({ severity: "success", summary: "Success", detail: "Account deactivated" });
      loadAccounts();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Failed to delete account",
      });
    }
  };

  // Get parent account options filtered by selected type
  const getParentOptions = () => {
    return accounts
      .filter((a) => a.type === form.type && (!editing || a.id !== editing.id))
      .map((a) => ({ label: `${a.code} - ${a.name}`, value: a.id }));
  };

  const typeTemplate = (node) => {
    const colors = { Asset: "info", Liability: "warning", Equity: "success", Revenue: "success", Expense: "danger" };
    return <Tag value={node.data.type} severity={colors[node.data.type]} />;
  };

  const balanceTemplate = (node) => {
    return <Tag value={node.data.normal_balance} severity={node.data.normal_balance === "debit" ? "info" : "success"} />;
  };

  const systemTemplate = (node) => {
    return node.data.is_system ? <i className="pi pi-lock text-500" title="System account" /> : null;
  };

  const actionsTemplate = (node) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => openEdit(node.data)}
          tooltip="Edit"
        />
        {!node.data.is_system && (
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-text p-button-danger p-button-sm"
            onClick={() => handleDelete(node.data)}
            tooltip="Deactivate"
          />
        )}
      </div>
    );
  };

  const leftToolbar = () => (
    <div className="flex flex-wrap gap-2 align-items-center">
      <Dropdown
        value={typeFilter}
        options={[{ label: "All Types", value: null }, ...ACCOUNT_TYPES]}
        onChange={(e) => handleTypeFilter(e.value)}
        placeholder="Filter by Type"
        className="w-12rem"
      />
    </div>
  );

  const rightToolbar = () => (
    <Button label="New Account" icon="pi pi-plus" className="p-button-sm p-button-success" onClick={openNew} />
  );

  if (loading && accounts.length === 0) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="mb-3">
        <h2 className="m-0">Chart of Accounts</h2>
        <p className="text-500 mt-1 mb-0">Manage your school's financial accounts</p>
      </div>

      <Toolbar className="mb-3" left={leftToolbar} right={rightToolbar} />

      <div className="card">
        <TreeTable
          value={treeData}
          loading={loading}
          emptyMessage="No accounts found."
          scrollable
          scrollHeight="calc(100vh - 320px)"
        >
          <Column field="code" header="Code" expander style={{ minWidth: "8rem" }} />
          <Column field="name" header="Account Name" style={{ minWidth: "14rem" }} />
          <Column header="Type" body={typeTemplate} style={{ minWidth: "8rem" }} />
          <Column header="Normal Balance" body={balanceTemplate} style={{ minWidth: "8rem" }} />
          <Column header="System" body={systemTemplate} style={{ width: "5rem", textAlign: "center" }} />
          <Column header="Actions" body={actionsTemplate} style={{ width: "8rem" }} />
        </TreeTable>
      </div>

      <Dialog
        header={editing ? "Edit Account" : "New Account"}
        visible={dialogVisible}
        style={{ width: "500px" }}
        modal
        onHide={() => setDialogVisible(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setDialogVisible(false)} />
            <Button label="Save" icon="pi pi-check" onClick={handleSave} loading={saving} />
          </div>
        }
      >
        <div className="flex flex-column gap-3 pt-2">
          <div className="flex flex-column gap-1">
            <label htmlFor="code">Account Code *</label>
            <InputText
              id="code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              disabled={editing?.is_system}
              placeholder="e.g. 1060"
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="name">Account Name *</label>
            <InputText
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Petty Cash"
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="type">Account Type *</label>
            <Dropdown
              id="type"
              value={form.type}
              options={ACCOUNT_TYPES}
              onChange={(e) => setForm({ ...form, type: e.value, parent_id: null })}
              disabled={editing?.is_system}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="parent">Parent Account</label>
            <Dropdown
              id="parent"
              value={form.parent_id}
              options={[{ label: "(None - Top Level)", value: null }, ...getParentOptions()]}
              onChange={(e) => setForm({ ...form, parent_id: e.value })}
              placeholder="Select parent account"
              filter
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="desc">Description</label>
            <InputTextarea
              id="desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
        </div>
      </Dialog>

      <Toast ref={toast} />
    </div>
  );
};

export default ChartOfAccountsPage;
